import React, { useCallback, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { auth } from "./config/firebase";
import routes from "./config/routes";
import "./css/style.css";
import ProtectedRoute from "./features/auth/components/ProtectedRoute";
import { AuthProvider, useAuth } from "./features/auth/context/AuthContext";
import { CalendarInitializer } from "./features/calendar/components/CalendarInitializer";
import ChitChatWindowsContainer from "./features/messages/components/ChitChatWindowsContainer";
import { ChitChatProvider } from "./features/messages/context/ChitChatContext";
import { useOnboardingRedirect } from "./features/onboarding/hooks/useOnboardingRedirect";
import { TasksInitializer } from "./features/tasks/components/TasksInitializer";
import { ProfanityModalProvider } from "./shared/components/ProfanityModalProvider";
import { ThemeProvider } from "./shared/context/ThemeContext";

/**
 * Main application component
 * Handles routing and authentication
 */
const App: React.FC = () => {
	const location = useLocation();

	// Reset scroll position on route change
	useEffect(() => {
		document.querySelector("html")!.style.scrollBehavior = "auto";
		window.scroll({ top: 0 });
		document.querySelector("html")!.style.scrollBehavior = "";
	}, [location.pathname]);

	return (
		<ThemeProvider>
			<ChitChatProvider>
				<AuthProvider>
					<ProfanityModalProvider />
					<AppRoutes />
					<ChitChatWindowsContainer />
				</AuthProvider>
			</ChitChatProvider>
		</ThemeProvider>
	);
};

/**
 * App routes component
 * Uses the auth context to determine which routes to show
 * and handles onboarding redirection
 */
const AppRoutes: React.FC = () => {
	const { isAuthenticated } = useAuth();
	const { checkOnboardingProgress } = useOnboardingRedirect();
	const location = useLocation();

	// Use a ref to track if we've already checked onboarding progress
	const hasCheckedRef = useRef(false);

	// Memoize the onboarding check function to prevent unnecessary re-renders
	const handleOnboardingCheck = useCallback(() => {
		// Get current Firebase user
		const currentUser = auth.currentUser;

		// Only check onboarding progress if:
		// 1. User is authenticated
		// 2. We haven't checked already in this session
		// 3. User has verified their email (for Firebase users)
		if (
			isAuthenticated &&
			!hasCheckedRef.current &&
			(!currentUser || currentUser.emailVerified)
		) {
			checkOnboardingProgress();
			hasCheckedRef.current = true;
		} else if (!isAuthenticated) {
			// Reset the check flag when user logs out
			hasCheckedRef.current = false;
		}
	}, [isAuthenticated, checkOnboardingProgress]);

	// Run the onboarding check only when auth state changes
	useEffect(() => {
		handleOnboardingCheck();
	}, [handleOnboardingCheck]);

	// Check if current path is a protected route
	const isProtectedRoute = () => {
		const currentPath = location.pathname;

		// Check if the current path matches any protected route
		const isProtected = routes.protected.some((route) => {
			// Check main route
			if (
				currentPath === route.path ||
				currentPath.startsWith(`${route.path}/`)
			) {
				return true;
			}

			// Check child routes if they exist
			const routeWithChildren = route as {
				path: string;
				children?: Array<{ path: string }>;
			};

			if (routeWithChildren.children) {
				return routeWithChildren.children.some(
					(childRoute) =>
						currentPath === `${route.path}/${childRoute.path}` ||
						currentPath.startsWith(`${route.path}/${childRoute.path}/`),
				);
			}

			return false;
		});

		return isProtected;
	};

	return (
		<>
			{/* Initialize tasks and calendar only when app loads for authenticated users on protected routes */}
			{isAuthenticated && isProtectedRoute() && (
				<>
					<TasksInitializer />
					<CalendarInitializer />
				</>
			)}

			<Routes>
				{/* Public routes - accessible to everyone */}
				{routes.public.map((route) => (
					<Route
						key={route.path}
						path={route.path}
						element={<route.component />}
					/>
				))}

				{/* Protected routes - require authentication */}
				{routes.protected.map((route) => {
					const routeWithChildren = route as {
						path: string;
						component: React.ComponentType<any>;
						children?: Array<{
							path: string;
							component: React.ComponentType<any>;
						}>;
					};

					// Handle nested routes if present
					if (
						routeWithChildren.children &&
						routeWithChildren.children.length > 0
					) {
						return routeWithChildren.children.map((childRoute) => {
							// Admin routes are now protected at the component level
							// No special handling needed here

							// Regular protected routes
							return (
								<Route
									key={`${routeWithChildren.path}/${childRoute.path}`}
									path={`${routeWithChildren.path}/${childRoute.path}`}
									element={
										<ProtectedRoute>
											<childRoute.component />
										</ProtectedRoute>
									}
								/>
							);
						});
					}

					// Handle regular routes
					return (
						<Route
							key={route.path}
							path={route.path}
							element={
								<ProtectedRoute>
									<route.component />
								</ProtectedRoute>
							}
						/>
					);
				})}

				{/* Onboarding routes - require authentication but are part of onboarding flow */}
				{routes.onboarding.map((route) => (
					<Route
						key={route.path}
						path={route.path}
						element={
							<ProtectedRoute>
								<route.component />
							</ProtectedRoute>
						}
					/>
				))}

				{/* Catch-all route - redirect to appropriate page based on auth status */}
				<Route
					path="*"
					element={
						isAuthenticated ? (
							<Navigate to="/not-found" replace />
						) : (
							<Navigate to="/signin" replace />
						)
					}
				/>
			</Routes>
		</>
	);
};

export default App;
